/**
 * Form helpers for create/edit school year views.
 */
import type {
  CreateSchoolYearDto,
  SchoolYear,
  UpdateSchoolYearDto,
} from '@/types/school-year.types';
import { toCalendarDateString } from '@/utils/format-date-utils';
import { getErrorMessage } from '@/utils/errors-utils';

/** Empty create form; dates stay unset until the user picks them. */
export function emptyCreateSchoolYearForm(): CreateSchoolYearDto {
  return {
    label: '',
    code: '',
    startDate: '',
    endDate: '',
    isCurrent: false,
    status: 'active',
  };
}

/** Patch one field; Date values are stored as `YYYY-MM-DD`. */
export function applySchoolYearFormField<T extends CreateSchoolYearDto | UpdateSchoolYearDto>(
  form: T,
  field: keyof T,
  value: string | boolean | Date | number | undefined,
): T {
  const finalValue = value instanceof Date ? value.toISOString().split('T')[0] : value;
  return { ...form, [field]: finalValue };
}

/** Attach existing years so the schema can reject overlapping ranges. */
export function withSchoolYearIntegrityContext<T extends object>(
  data: T,
  existingYears: unknown[],
  excludeId?: string,
): T & { _existingYears: unknown[]; _excludeId?: string } {
  return {
    ...data,
    _existingYears: existingYears,
    ...(excludeId ? { _excludeId: excludeId } : {}),
  };
}

/** Hydrate the edit form from GET school year. */
export function schoolYearToFormData(schoolYear: SchoolYear): UpdateSchoolYearDto {
  return {
    code: schoolYear.code,
    startDate: toCalendarDateString(schoolYear.startDate),
    endDate: toCalendarDateString(schoolYear.endDate),
    isCurrent: schoolYear.isCurrent,
    status: schoolYear.status,
    establishmentObjective: schoolYear.establishmentObjective ?? undefined,
  };
}

/** Empty string → `undefined` (omit from the DTO). */
export function parseEstablishmentObjective(raw: string): number | undefined {
  return raw === '' ? undefined : Number(raw);
}

/** Number input display; empty when unset. */
export function formatEstablishmentObjectiveInput(value: number | null | undefined): string {
  if (value === undefined || value === null) return '';
  return String(value);
}

/** Create DTO; establishment objective is admin-only. */
export function buildCreateSchoolYearDto(
  form: CreateSchoolYearDto,
  currentUserRoleCode?: string,
): CreateSchoolYearDto {
  return {
    ...form,
    establishmentObjective:
      currentUserRoleCode === 'admin' && form.establishmentObjective !== undefined
        ? Number(form.establishmentObjective)
        : undefined,
  };
}

/** Update DTO; non-admins cannot send establishment objective. */
export function buildUpdateSchoolYearDto(
  form: UpdateSchoolYearDto,
  currentUserRoleCode?: string,
): UpdateSchoolYearDto {
  const payload: UpdateSchoolYearDto = { ...form };
  if (currentUserRoleCode !== 'admin') {
    delete payload.establishmentObjective;
    return payload;
  }
  if (payload.establishmentObjective !== undefined) {
    payload.establishmentObjective = Number(payload.establishmentObjective);
  }
  return payload;
}

/** Central error copy from {@link getErrorMessage}. */
export function getSchoolYearApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}
